import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { StickyNote as StickyNoteType, FileAttachment } from '../../types';
import { useWhiteboardStore } from '../../store/useWhiteboardStore';
import { uploadFile } from '../../utils/fileUploadApi';
import { incrementUsage } from '../../utils/firestoreApi';
import { GLASSMORPHIC_COLORS, getGlassmorphicColorBySolid } from '../../utils/colorPalette';
import { trackEvent } from '../../utils/analytics';
import { useFeatureAccess } from '../../hooks/useQuota';
import { useAuth } from '../../hooks/useAuth';

import './StickyNote.css';
import { StatusBadge } from '../StatusBadge';
import MergeProposalModal from '../MergeProposalModal';
import TopicEditModal from './TopicEditModal';
import SystemPromptDialog from '../SystemPromptDialog';
import AlertModal from '../AlertModal';

interface StickyNoteProps {
  note: StickyNoteType;
  onMergeCompleted?: (message: string) => void;
  isChatOpen?: boolean;
}

const StickyNote: React.FC<StickyNoteProps> = ({ note, onMergeCompleted, isChatOpen = false }) => {
  const { updateNote, selectNote, deleteNote, addBranchNote } = useWhiteboardStore();
  const { selectedNoteIds, scale, canvasId } = useWhiteboardStore((state) => state.state);
  const { user } = useAuth();
  const { canUploadFile, canPerformMerge } = useFeatureAccess();
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(note.content);
  const [topic, setTopic] = useState(note.topic || 'New Topic');
  const noteRef = useRef<HTMLDivElement>(null);
  const lastPositionRef = useRef<{ x: number; y: number }>(note.position);
  const isSelected = selectedNoteIds.includes(note.id);
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [hasDragged, setHasDragged] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showTopicEditModal, setShowTopicEditModal] = useState(false);
  const [showSystemPromptDialog, setShowSystemPromptDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Alert modal state
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalConfig, setAlertModalConfig] = useState({
    title: '',
    message: '',
    buttonText: 'OK',
    buttonVariant: 'primary' as 'primary' | 'danger' | 'secondary' | 'success'
  });
  
  // Color picker state
  const [showColorPicker, setShowColorPicker] = useState(false);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  
  // File upload state
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setContent(note.content);
  }, [note.content]);

  useEffect(() => {
    //console.log('📝 Topic useEffect triggered:', { noteId: note.id, noteTopic: note.topic, currentLocalTopic: topic });
    setTopic(note.topic || 'New Topic');
  }, [note.topic]);

  // Update lastPositionRef when note position changes
  useEffect(() => {
    lastPositionRef.current = note.position;
  }, [note.position]);

  // Native drag handlers
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (isEditing) return;
    
    // Check if the click target is the title element
    const target = e.target as HTMLElement;
    if (target.classList.contains('title')) {
      return; // Don't start dragging if clicking on title
    }
    
    setDragging(true);
    setHasDragged(false); // Reset drag state
    const rect = noteRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
    target.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging || isEditing) return;
    const parent = noteRef.current?.parentElement;
    if (!parent) return;
    const parentRect = parent.getBoundingClientRect();
    let newX = (e.clientX - parentRect.left - dragOffset.x) / scale;
    let newY = (e.clientY - parentRect.top - dragOffset.y) / scale;
    newX = Math.max(0, Math.min(newX, parent.offsetWidth - note.width));
    newY = Math.max(0, Math.min(newY, parent.offsetHeight - note.height));
    
    // Only update if position actually changed significantly to prevent infinite loops
    const lastPos = lastPositionRef.current;
    if (Math.abs(newX - lastPos.x) > 1 || Math.abs(newY - lastPos.y) > 1) {
      setHasDragged(true); // Mark that we've actually moved the note
      lastPositionRef.current = { x: newX, y: newY };
      updateNote(note.id, {
        position: { x: newX, y: newY },
      });
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    setDragging(false);
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Don't select the note if it was just dragged
    if (hasDragged) {
      setHasDragged(false); // Reset for next interaction
      return;
    }
    
    selectNote(note.id, e.ctrlKey || e.metaKey);
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Don't open modal if it was just dragged
    if (hasDragged) {
      setHasDragged(false); // Reset for next interaction
      return;
    }
    
    // Select the note and open topic edit modal
    selectNote(note.id, false);
    setShowTopicEditModal(true);
  };

  const handleTitleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Select the note when clicking on title
    selectNote(note.id, false);
  };

  const handleTitleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Open topic edit modal directly when double-clicking title
    selectNote(note.id, false);
    setShowTopicEditModal(true);
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
  };

  const handleBlur = () => {
    setIsEditing(false);
    updateNote(note.id, { content });
    trackEvent('sticky_note_edited', { character_count: content.length });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsEditing(false);
      setContent(note.content);
    } else if (e.key === 'Enter' && !e.shiftKey) {
      setIsEditing(false);
      updateNote(note.id, { content });
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    setIsDeleting(true);
    // Add fade-out animation delay
    setTimeout(() => {
      deleteNote(note.id);
    }, 300); // 300ms for fade animation
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  // Color picker handlers
  const handleColorPickerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowColorPicker(!showColorPicker);
  };

  const handleColorSelect = (color: string) => {
    updateNote(note.id, { color });
    setShowColorPicker(false);
  };

  const handleColorPickerClose = () => {
    setShowColorPicker(false);
  };

  const handleProposeMerge = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMergeModal(true);
  };

  // Helper function to show alert modal
  const showAlert = (title: string, message: string, buttonVariant: 'primary' | 'danger' | 'secondary' | 'success' = 'primary') => {
    setAlertModalConfig({
      title,
      message,
      buttonText: 'OK',
      buttonVariant
    });
    setShowAlertModal(true);
  };

  const handleSaveTopic = (newTopic: string) => {

    
    // Only set hasUserSetTopic if user actually set a meaningful topic
    const isMeaningfulTopic = Boolean(newTopic.trim()) && newTopic !== 'New Topic';
    
    updateNote(note.id, { 
      topic: newTopic, 
      hasUserSetTopic: isMeaningfulTopic 
    });
  };

  const handleSaveSystemPrompt = (originalSystemPrompt: string, extractedPersona: string, welcomeMessage?: string, curatedSystemPrompt?: string) => {
    const currentChatHistory = note.chatHistory || [];
    const hasExistingConversation = currentChatHistory.length > 0;
    

    
    if (hasExistingConversation) {
      // For existing conversations, add an implicit persona change message
      const personaChangeMessage = `I want you to switch to being a ${extractedPersona}. Please acknowledge this change and continue our conversation with your new persona.`;
      
      // Add the user message and optionally the AI's response if provided
      const updatedChatHistory = [
        ...currentChatHistory,
        { sender: 'user' as const, text: personaChangeMessage }
      ];
      
      // If we have a welcome message from the persona switch, add it as AI response
      if (welcomeMessage) {
        updatedChatHistory.push({ sender: 'ai' as const, text: welcomeMessage });
      }
      
      updateNote(note.id, { 
        systemPrompt: curatedSystemPrompt || originalSystemPrompt,
        persona: extractedPersona,
        hasCustomSystemPrompt: true,
        chatHistory: updatedChatHistory
      });
    } else {
      // For new conversations, add the welcome message
      updateNote(note.id, { 
        systemPrompt: curatedSystemPrompt || originalSystemPrompt,
        persona: extractedPersona,
        hasCustomSystemPrompt: true,
        chatHistory: [{ sender: 'ai', text: welcomeMessage || `Hi, I am JJ, your ${extractedPersona} assistant. How can I help you today?` }]
      });
    }
    
    setShowSystemPromptDialog(false);
  };

  // File upload handlers
  const handleFileIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      handleFileUpload(Array.from(files));
    }
    // Reset input to allow same file selection again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  const handleFileUpload = async (files: File[]) => {
    const currentFiles = note.attachedFiles || [];
    
    // Check quota if user is authenticated and canvas ID is available
    if (user && canvasId) {
      try {
        const canUpload = await canUploadFile();
        if (!canUpload) {
          showAlert(
            'File Attachment Limit Reached',
            'You\'ve reached your file attachment limit. Please upgrade your plan to upload more files.',
            'danger'
          );
          return;
        }
      } catch (error) {
        console.error('Error checking file upload quota:', error);
        // Fallback: allow upload even if quota check fails
      }
    }
    
    // Check file limits
    if (currentFiles.length + files.length > 3) {
      showAlert(
        'File Limit Exceeded',
        'Maximum 3 files allowed per note',
        'danger'
      );
      return;
    }
    
    setIsUploading(true);
    
    try {
      // Upload each file using our real upload API
      const uploadPromises = files.map(async (file) => {

        
        try {
          const uploadedFile = await uploadFile(file, (progress) => {

          });
          
          
          
                  // Convert to FileAttachment format
        const fileAttachment: FileAttachment = {
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: uploadedFile.originalName, // Use original filename for display
          fileId: uploadedFile.fileName, // Store backend file ID for API calls
          size: uploadedFile.size,
          type: uploadedFile.fileType,
          uploadDate: new Date(),
          uploadStatus: 'completed' as const,
          url: uploadedFile.downloadUrl
        };
          
          return fileAttachment;
        } catch (error) {
          console.error('❌ StickyNote: Upload failed for', file.name, error);
          
          // Create failed file attachment
          const failedAttachment: FileAttachment = {
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            name: file.name,
            size: file.size,
            type: file.type,
            uploadDate: new Date(),
            uploadStatus: 'failed' as const,
            error: error instanceof Error ? error.message : 'Upload failed'
          };
          
          return failedAttachment;
        }
      });
      
      // Wait for all uploads to complete
      const newFileAttachments = await Promise.all(uploadPromises);
      
      // Update note with uploaded files
      updateNote(note.id, {
        attachedFiles: [...currentFiles, ...newFileAttachments]
      });
      
      // Increment user quota for file attachments if user is authenticated
      if (user) {
        try {
          await incrementUsage(user.uid, 'fileAttachments');
        } catch (error) {
          console.error('Error incrementing file attachments quota:', error);
        }
      }
      
      setIsUploading(false);
      
      // Show success/failure summary
      const successful = newFileAttachments.filter(f => f.uploadStatus === 'completed').length;
      const failed = newFileAttachments.filter(f => f.uploadStatus === 'failed').length;
      
      if (successful > 0 && failed === 0) {

      } else if (successful > 0 && failed > 0) {
        showAlert(
          'Partial Upload Success',
          `${successful} files uploaded successfully, ${failed} failed. Check console for details.`,
          'secondary'
        );
      } else {
        showAlert(
          'Upload Failed',
          'All file uploads failed. Please try again.',
          'danger'
        );
      }
      
    } catch (error) {
      console.error('💥 StickyNote: Upload process failed:', error);
      setIsUploading(false);
      showAlert(
        'Upload Failed',
        'File upload failed. Please try again.',
        'danger'
      );
    }
  };

  const handleMerge = async (summary: string, mergeTarget: StickyNoteType) => {
    // Check quota if user is authenticated and canvas ID is available
    if (user && canvasId) {
      try {
        const canMerge = await canPerformMerge();
        if (!canMerge) {
          showAlert(
            'Merge Operation Limit Reached',
            'You\'ve reached your merge operation limit. Please upgrade your plan to perform more merges.',
            'danger'
          );
          return;
        }
      } catch (error) {
        console.error('Error checking merge quota:', error);
        // Fallback: allow merge even if quota check fails
      }
    }

    // TODO: Implement the actual merge logic
    // For now, we'll just update the branch note status to 'merged'
    const { mergeBranchToParent } = useWhiteboardStore.getState();
    mergeBranchToParent(note.id);
    
    
    useWhiteboardStore.getState().updateNote(note.id, { 
      showConverge: true,
      showConvergeGlow: true, // Add glow effect
      mergeTargetId: mergeTarget.id // Store which note this was merged into
    });
    
    // Add the summary to the merge target note's chat history
    const targetNote = useWhiteboardStore.getState().state.notes.find(n => n.id === mergeTarget.id);
    if (targetNote) {
      const currentChatHistory = targetNote.chatHistory || [];
      const newMessageIndex = currentChatHistory.length;
      const updatedChatHistory = [
        ...currentChatHistory,
        { sender: 'ai' as const, text: summary }
      ];
      
      // Get the current merge messages metadata
      const currentMergeMessages = targetNote.mergeMessages || [];
      const updatedMergeMessages = [
        ...currentMergeMessages,
        {
          messageIndex: newMessageIndex,
          sourceBranchId: note.id,
          sourceBranchName: note.topic || 'Unknown Branch'
        }
      ];
      
      useWhiteboardStore.getState().updateNote(mergeTarget.id, {
        chatHistory: updatedChatHistory,
        mergeMessages: updatedMergeMessages
      });
      
      // Increment canvas quota for merge operations if user is authenticated
      if (user && canvasId) {
        try {
          await incrementUsage(user.uid, 'totalThreads');
        } catch (error) {
          console.error('Error incrementing canvas quota:', error);
        }
      }
      
      // Show success toast via callback
      if (onMergeCompleted) {
        onMergeCompleted(`Successfully merged "${note.topic || 'Untitled'}" into "${mergeTarget.topic || 'Untitled'}"`);
      }
    }
  };

  // Remove isNewBranch after animation
  useEffect(() => {
    if (note.isNewBranch) {
      const timeout = setTimeout(() => {
        updateNote(note.id, { isNewBranch: false });
      }, 1200);
      return () => clearTimeout(timeout);
    }
  }, [note.isNewBranch, note.id, updateNote]);

  // Remove converge glow after 1 second
  useEffect(() => {
    if (note.showConvergeGlow) {
      const timeout = setTimeout(() => {
        updateNote(note.id, { showConvergeGlow: false });
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [note.showConvergeGlow, note.id, updateNote]);

  // Close color picker when clicking outside
  useEffect(() => {
    if (!showColorPicker) return;
    
    function handleClickAway(event: MouseEvent) {
      const colorPicker = colorPickerRef.current;
      if (colorPicker && !colorPicker.contains(event.target as Node)) {
        handleColorPickerClose();
      }
    }
    
    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [showColorPicker]);

  // Get status badge info
  const getStatusInfo = () => {
    if (!note.parentId) {
      return { text: 'Root', color: '#3B82F6', bgColor: '#3B82F6' }; // blue-500
    } else if (note.showConverge) {
      return { text: 'Merged', color: '#6B7280', bgColor: '#6B7280' }; // gray-500
    } else {
      return { text: 'Active', color: '#EAB308', bgColor: '#EAB308' }; // yellow-500
    }
  };

  const statusInfo = getStatusInfo();
  const attachedFileCount = note.attachedFiles ? note.attachedFiles.filter(f => f.uploadStatus === 'completed').length : 0;
  
  // Get current color info
  const currentColor = getGlassmorphicColorBySolid(note.color || '#FFF0F0');

  // Get corresponding border color for background color
  const getBorderColor = (bgColor: string) => {
    const borderColorMap: Record<string, string> = {
      '#FFFDE7': '#FEF3C7', // yellow-50 -> yellow-200
      '#FFF3E0': '#FED7AA', // orange-50 -> orange-200
      '#FFEBEE': '#FECACA', // red-100 -> red-200
      '#FCE4EC': '#FBCFE8', // pink-50 -> pink-200
      '#F3E5F5': '#E9D5FF', // purple-50 -> purple-200
      '#EDE7F6': '#DDD6FE', // deep-purple-50 -> deep-purple-200
      '#E8EAF6': '#C7D2FE', // indigo-50 -> indigo-200
      '#E3F2FD': '#BFDBFE', // blue-50 -> blue-200
      '#E1F5FE': '#BAE6FD', // light-blue-50 -> light-blue-200
      '#E0F7FA': '#A5F3FC', // cyan-50 -> cyan-200
      '#E0F2F1': '#99F6E4', // teal-50 -> teal-200
      '#E8F5E9': '#BBF7D0', // green-50 -> green-200
      '#F1F8E9': '#D9F99D', // light-green-50 -> light-green-200
      '#F9FBE7': '#FDE68A', // lime-50 -> lime-200
      '#FFFFFF': '#E5E7EB', // white -> gray-200
    };
    return borderColorMap[bgColor] || '#E5E7EB'; // fallback to gray-200
  };

  return (
    <div
      ref={noteRef}
      data-note-id={note.id}
      className={`sticky-note${note.isCreatingBranch ? ' creating-branch' : ''}${note.isNewBranch ? ' new-branch' : ''}${isSelected ? ' selected' : ''}${isDeleting ? ' deleting' : ''}${isChatOpen ? ' chat-open' : ''}${dragging ? ' dragging' : ''}`}
      style={{
        width: `${note.width}px`,
        height: `${note.height}px`,
        zIndex: note.zIndex,
        position: 'absolute',
        left: note.position.x,
        top: note.position.y,
        touchAction: 'none',
        cursor: dragging ? 'grabbing' : 'grab',
        backgroundColor: note.color ? note.color : '#FFF0F0', // Completely opaque background
        borderColor: note.color ? getBorderColor(note.color) : '#FED7AA', // Corresponding Tailwind border color
        borderStyle: isDragOver ? 'dashed' : 'solid',
        borderWidth: isDragOver ? '2px' : '1px',
        transform: dragging ? 'scale(1.05)' : 'scale(1)',
        transition: dragging ? 'none' : 'transform 0.2s ease, box-shadow 0.2s ease',
        boxShadow: dragging 
          ? '0 25px 50px -12px rgb(0 0 0 / 0.25)' // shadow-2xl
          : '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)', // shadow-lg
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onMouseEnter={() => {}}
      onMouseLeave={() => {}}
    >
      {showMergeModal && note.parentId && (
        <MergeProposalModal
          branchNote={note}
          parentNote={useWhiteboardStore.getState().state.notes.find(n => n.id === note.parentId)!}
          isOpen={showMergeModal}
          onClose={() => setShowMergeModal(false)}
          onMerge={handleMerge}
        />
      )}
      
      <TopicEditModal
        isOpen={showTopicEditModal}
        onClose={() => setShowTopicEditModal(false)}
        onSave={handleSaveTopic}
        currentTopic={note.topic || 'New Topic'}
      />
      
      <SystemPromptDialog
        isOpen={showSystemPromptDialog}
        onClose={() => setShowSystemPromptDialog(false)}
        onSave={handleSaveSystemPrompt}
        currentPrompt={note.systemPrompt || ''}
        hasExistingConversation={(note.chatHistory && note.chatHistory.length > 0) || false}
      />

      {/* Status Badge and Attachment Count - Top Left */}
      <div className="status-container">
        <div className={`status-badge ${statusInfo.text.toLowerCase()}`}>
          {statusInfo.text}
        </div>
        {attachedFileCount > 0 && (
          <div className="attachment-pill" title={`${attachedFileCount} file(s) attached`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <span>{attachedFileCount}</span>
          </div>
        )}
      </div>

        {/* Color Picker Button - Top Right */}
        <button 
          className="color-picker-btn" 
          onClick={handleColorPickerClick}
          title="Change note color"
          style={{
            position: 'absolute',
            top: '8px',
            right: note.parentId ? '80px' : '80px', // Consistent positioning for both root and branch notes
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            border: '2px solid',
            borderColor: '#64748b', // slate-500 (modern gray)
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            transition: 'all 0.3s ease',
            zIndex: 10,
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          🖌️
        </button>

        {/* Persona Icon - Top Right */}
        <button 
          className="persona-btn" 
          onClick={() => setShowSystemPromptDialog(true)}
          title={note.hasCustomSystemPrompt || note.inheritedSystemPrompt 
            ? `Current persona: ${note.persona || note.inheritedSystemPrompt}. Click to change.` 
            : "Choose AI Persona"
          }
          style={{
            position: 'absolute',
            top: '8px',
            right: '44px', // Consistent positioning for both root and branch notes
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            border: '2px solid',
            borderColor: (note.hasCustomSystemPrompt || note.inheritedSystemPrompt) ? '#1976d2' : '#ff9800',
            backgroundColor: (note.hasCustomSystemPrompt || note.inheritedSystemPrompt) 
              ? 'rgba(25, 118, 210, 0.1)' 
              : 'rgba(255, 152, 0, 0.1)',
            color: (note.hasCustomSystemPrompt || note.inheritedSystemPrompt) ? '#1976d2' : '#ff9800',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            transition: 'all 0.3s ease',
            zIndex: 10
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = (note.hasCustomSystemPrompt || note.inheritedSystemPrompt)
              ? 'rgba(25, 118, 210, 0.2)'
              : 'rgba(255, 152, 0, 0.2)';
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = (note.hasCustomSystemPrompt || note.inheritedSystemPrompt)
              ? 'rgba(25, 118, 210, 0.1)'
              : 'rgba(255, 152, 0, 0.1)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          {note.hasCustomSystemPrompt || note.inheritedSystemPrompt ? '👤' : '⚡'}
        </button>

      {/* Main Content Area - Chat Icon, Title, and Persona with bottom padding for actions */}
      <div className="sticky-note-middle" style={{ paddingBottom: '60px' }}>
        {/* Chat Bubble Icon */}
        <div className={`chat-icon-circle ${isChatOpen ? 'chat-active' : ''}`}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={isChatOpen ? "#10B981" : "#3B82F6"} strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          {isChatOpen && (
            <div className="chat-active-indicator">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#10B981">
                <circle cx="12" cy="12" r="12" />
              </svg>
            </div>
          )}
        </div>

        {/* Title - Now allowed to wrap to 2 lines */}
        <div 
          className="title" 
          onClick={handleTitleClick}
          onDoubleClick={handleTitleDoubleClick}
          onPointerDown={(e) => e.stopPropagation()}
          title="Double-click to edit title"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: '1.2',
            minHeight: '1.2em',
            maxHeight: '2.4em'
          }}
        >
          {topic && topic !== 'New Topic' ? topic : 'New Note'}
        </div>

        {/* Persona Subtitle */}
        {(note.persona || note.inheritedSystemPrompt) && (
          <div className="persona-subtitle">{note.persona || note.inheritedSystemPrompt}</div>
        )}
      </div>

      {/* Action Buttons - Absolutely positioned at bottom */}
      <div className="sticky-note-actions" style={{
        position: 'absolute',
        bottom: '12px',
        left: '12px',
        right: '12px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',
        zIndex: 5
      }}>


        {/* Action Buttons - Centered when no attachments, Right-aligned when attachments exist */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button 
            className="chat-button"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: 'white',
              color: '#374151',
              fontSize: '14px',
              fontWeight: '600',
              padding: '8px 20px',
              borderRadius: '9999px',
              border: 'none',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f9fafb';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span>Chat</span>
          </button>
          
          {/* Merge Button - only for active branch notes */}
          {note.parentId && !note.showConverge && (
            <button
              onClick={handleProposeMerge}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: '#22c55e',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                padding: '8px 20px',
                borderRadius: '9999px',
                border: 'none',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#16a34a';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#22c55e';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="7" cy="6" r="2" stroke="white" strokeWidth="2" fill="none"/>
                <circle cx="7" cy="18" r="2" stroke="white" strokeWidth="2" fill="none"/>
                <circle cx="17" cy="12" r="2" stroke="white" strokeWidth="2" fill="none"/>
                <path d="M7 8v8a5 5 0 0 0 5 5h5" stroke="white" strokeWidth="2" fill="none"/>
                <path d="M7 8V6" stroke="white" strokeWidth="2" fill="none"/>
                <path d="M17 14v-2a5 5 0 0 0-5-5H7" stroke="white" strokeWidth="2" fill="none"/>
              </svg>
              <span>Merge</span>
            </button>
          )}
        </div>
      </div>

      {/* Color Picker Popover */}
      {showColorPicker && ReactDOM.createPortal(
        <div
          ref={colorPickerRef}
          className="sticky-note-color-picker"
          style={{
            position: 'absolute',
            top: noteRef.current ? noteRef.current.getBoundingClientRect().top + 40 : 0,
            left: noteRef.current ? noteRef.current.getBoundingClientRect().left : 0,
            zIndex: 2000,
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            padding: '12px',
            border: '1px solid #e5e7eb',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)'
          }}
        >
          <div className="color-picker-header" style={{ marginBottom: '8px', fontSize: '12px', fontWeight: '600', color: '#374151' }}>
            Choose Color
          </div>
          <div className="color-picker-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '8px',
            alignItems: 'center',
            justifyItems: 'center'
          }}>
            {GLASSMORPHIC_COLORS.map((color) => (
              <button
                key={color.id}
                className="color-picker-option"
                style={{ 
                  background: color.bg,
                  border: `2px solid ${color.border}`,
                  borderRadius: '8px',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  width: '28px',
                  height: '24px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
                onClick={() => handleColorSelect(color.solid)}
                title={color.name}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {note.color === color.solid && (
                  <div style={{
                    position: 'absolute',
                    top: '-2px',
                    right: '-2px',
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: '#3B82F6',
                    border: '1px solid white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
                  }}>
                    <svg width="6" height="6" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20,6 9,17 4,12"></polyline>
                    </svg>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}

      {/* Hidden file input for uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Delete button - for all notes (root and branch) */}
      <button 
        className="delete-branch-btn" 
        onClick={handleDelete} 
        title={note.parentId ? "Delete branch" : "Delete idea starter"}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            color: '#f44336',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease',
            zIndex: 10
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 1)';
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          ✕
        </button>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && ReactDOM.createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={handleCancelDelete}
        >
          <div 
            style={{
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              padding: '24px',
              borderRadius: '16px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              maxWidth: '400px',
              textAlign: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', color: '#1f2937', fontSize: '20px', fontWeight: '600' }}>
              {note.parentId ? 'Delete Branch' : 'Delete Idea Starter'}
            </h3>
            <p style={{ margin: '0 0 24px 0', color: '#6b7280', lineHeight: '1.5', fontSize: '14px' }}>
              Are you sure you want to delete this {note.parentId ? 'branch' : 'idea starter'}?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={handleCancelDelete}
                style={{
                  padding: '10px 20px',
                  border: '1px solid rgba(209, 213, 219, 0.8)',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  color: '#6b7280',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.borderColor = 'rgba(156, 163, 175, 0.8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.7)';
                  e.currentTarget.style.borderColor = 'rgba(209, 213, 219, 0.8)';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '8px',
                  background: 'rgba(239, 68, 68, 0.9)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(220, 38, 38, 0.9)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.9)';
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Alert Modal */}
      {showAlertModal && (
        <AlertModal
          isOpen={showAlertModal}
          title={alertModalConfig.title}
          message={alertModalConfig.message}
          buttonText={alertModalConfig.buttonText}
          buttonVariant={alertModalConfig.buttonVariant}
          onClose={() => setShowAlertModal(false)}
        />
      )}
    </div>
  );
};

export default StickyNote;
